import React, { useCallback, useEffect, useState } from 'react';
import {
  Spin, Button, Card, Typography, Tabs, Input, Tag, Upload, Radio, Modal,
  InputNumber, Select, Empty, theme,
} from 'antd';
import type { UploadFile } from 'antd';
import { IconFileSpreadsheet, IconPlus, IconUpload, IconTrash, IconPencil } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { message } from '../../services/toastMessage';
import AppLayout from '../../components/Layouts/Layout';
import EmptyState from '../../components/EmptyState';
import PageHeaderRow from '../../components/Layouts/PageHeaderRow';
import ConfirmBottomSheet from '../../components/ConfirmBottomSheet';
import AssistantGuideEditor from '../../components/quotes/AssistantGuideEditor';
import VisitFeeEditor from '../../components/quotes/VisitFeeEditor';
import {
  uploadRateSheet, getMyRateSheets, deleteRateSheet,
  getMyRateItems, createRateItem, updateRateItem, deleteRateItem,
} from '../../services/ProviderRateSheetService';
import {
  IProviderRateDocument, IProviderRateItem, IUpsertRateItemRequest,
  RateItemKind, RATE_ITEM_UNITS,
} from '../../interfaces/heavyDutyMachinery/IProviderRateSheet';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { useAppBreakpoint } from '../../hooks/useAppBreakpoint';
import { TEAL, TEAL_SOFT_BG, SURFACE_1 } from '../../theme/creamyTokens';

const { Text, Paragraph } = Typography;
const { Search } = Input;

/** A document still being read is re-checked on this cadence -- the server sweeps every 30s. */
const POLL_INTERVAL_MS = 8000;

/**
 * Reading / waiting / done / broken. The amber pair comes from antd's own
 * warning tokens rather than the creamy palette's AMBER, which lives on a
 * branch this one does not depend on -- and which would collide the day both
 * land.
 */
const statusTones = (
  token: ReturnType<typeof theme.useToken>['token'],
): Record<string, { bg: string; color: string }> => ({
  Analyzing: { bg: token.colorWarningBg, color: token.colorWarning },
  PendingReview: { bg: TEAL_SOFT_BG, color: TEAL },
  Confirmed: { bg: SURFACE_1, color: token.colorTextSecondary },
  Failed: { bg: token.colorErrorBg, color: token.colorError },
});

const STATUS_LABEL_KEYS: Record<string, string> = {
  Analyzing: TRANSLATIONS.RATE_SHEET_STATUS_ANALYZING,
  PendingReview: TRANSLATIONS.RATE_SHEET_STATUS_PENDING_REVIEW,
  Confirmed: TRANSLATIONS.RATE_SHEET_STATUS_CONFIRMED,
  Failed: TRANSLATIONS.RATE_SHEET_STATUS_FAILED,
};

const KIND_LABEL_KEYS: Record<RateItemKind, string> = {
  Material: TRANSLATIONS.RATE_ITEM_KIND_MATERIAL,
  Labour: TRANSLATIONS.RATE_ITEM_KIND_LABOUR,
  Travel: TRANSLATIONS.RATE_ITEM_KIND_TRAVEL,
};

const EMPTY_ITEM: IUpsertRateItemRequest = { kind: 'Material', title: '', unit: 'ud', unitPrice: 0 };

/**
 * "Mi tarifa" -- what this provider charges, and the documents it came from.
 *
 * Two tabs rather than two pages because a provider thinks of this as one
 * thing. The catalogue is what matters day to day; the uploads are how it got
 * there, and they are worth keeping visible only so a bad import can be traced
 * back to the file that caused it.
 *
 * The upload asks one question before it will take the file: whether the
 * prices include VAT. It is not a nicety. This platform quotes VAT-inclusive
 * and a trade price list almost never is, so guessing wrong would leave
 * somebody's entire catalogue under what they meant to charge with nothing on
 * screen to show for it. Cheaper to ask.
 */
const MyRateCatalogContent: React.FC = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { isMobile } = useAppBreakpoint();
  const navigate = useNavigate();

  const [documents, setDocuments] = useState<IProviderRateDocument[]>([]);
  const [items, setItems] = useState<IProviderRateItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState<string | undefined>(undefined);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState<UploadFile | null>(null);
  const [pricesIncludeVat, setPricesIncludeVat] = useState<boolean | null>(null);
  const [uploading, setUploading] = useState(false);

  const [editing, setEditing] = useState<IProviderRateItem | null>(null);
  const [draft, setDraft] = useState<IUpsertRateItemRequest>(EMPTY_ITEM);
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [savingItem, setSavingItem] = useState(false);

  const [deletingItem, setDeletingItem] = useState<IProviderRateItem | null>(null);
  const [deletingDocument, setDeletingDocument] = useState<IProviderRateDocument | null>(null);

  const loadItems = useCallback(async (term?: string) => {
    try {
      setItems(await getMyRateItems(term));
    } catch {
      message.error(translate(t, TRANSLATIONS.RATE_CATALOG_LOAD_ERROR));
    }
  }, [t]);

  const loadDocuments = useCallback(async () => {
    try {
      setDocuments(await getMyRateSheets());
    } catch {
      message.error(translate(t, TRANSLATIONS.RATE_CATALOG_LOAD_ERROR));
    }
  }, [t]);

  useEffect(() => {
    Promise.all([loadItems(), loadDocuments()]).finally(() => setLoading(false));
  }, [loadItems, loadDocuments]);

  // A document being read finishes on the server's own schedule, and the
  // person who uploaded it is watching the screen. Polling stops the moment
  // nothing is still analyzing, so an idle page makes no requests at all.
  useEffect(() => {
    if (!documents.some((d) => d.status === 'Analyzing')) return undefined;
    const timer = setInterval(loadDocuments, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [documents, loadDocuments]);

  const handleUpload = async () => {
    if (!pendingFile?.originFileObj || pricesIncludeVat === null) return;
    setUploading(true);
    try {
      await uploadRateSheet(pendingFile.originFileObj as File, pricesIncludeVat);
      message.success(translate(t, TRANSLATIONS.RATE_SHEET_UPLOAD_STARTED));
      setUploadOpen(false);
      setPendingFile(null);
      setPricesIncludeVat(null);
      await loadDocuments();
    } catch (error: any) {
      message.error(error?.message || translate(t, TRANSLATIONS.RATE_SHEET_UPLOAD_ERROR));
    } finally {
      setUploading(false);
    }
  };

  const openNewItem = () => {
    setEditing(null);
    setDraft(EMPTY_ITEM);
    setItemModalOpen(true);
  };

  const openEditItem = (item: IProviderRateItem) => {
    setEditing(item);
    setDraft({
      reference: item.reference,
      kind: item.kind,
      title: item.title,
      subtitle: item.subtitle,
      category: item.category,
      unit: item.unit,
      unitPrice: item.unitPrice,
    });
    setItemModalOpen(true);
  };

  const handleSaveItem = async () => {
    if (!draft.title.trim()) {
      message.error(translate(t, TRANSLATIONS.RATE_ITEM_TITLE_REQUIRED));
      return;
    }
    if (!draft.unitPrice || draft.unitPrice <= 0) {
      message.error(translate(t, TRANSLATIONS.RATE_ITEM_PRICE_REQUIRED));
      return;
    }
    setSavingItem(true);
    try {
      if (editing) await updateRateItem(editing.id, draft);
      else await createRateItem(draft);
      message.success(translate(t, TRANSLATIONS.RATE_ITEM_SAVED));
      setItemModalOpen(false);
      await loadItems(search);
    } catch (error: any) {
      message.error(error?.message || translate(t, TRANSLATIONS.RATE_ITEM_SAVE_ERROR));
    } finally {
      setSavingItem(false);
    }
  };

  const handleDeleteItem = async () => {
    if (!deletingItem) return;
    try {
      await deleteRateItem(deletingItem.id);
      setDeletingItem(null);
      await loadItems(search);
    } catch {
      message.error(translate(t, TRANSLATIONS.RATE_ITEM_SAVE_ERROR));
    }
  };

  const handleDeleteDocument = async () => {
    if (!deletingDocument) return;
    try {
      await deleteRateSheet(deletingDocument.id);
      setDeletingDocument(null);
      await loadDocuments();
    } catch {
      message.error(translate(t, TRANSLATIONS.RATE_SHEET_UPLOAD_ERROR));
    }
  };

  const statusPill = (status: string) => {
    const tones = statusTones(token);
    const tone = tones[status] ?? tones.Confirmed;
    return (
      <span style={{ background: tone.bg, color: tone.color, borderRadius: 999, padding: '3px 10px', fontSize: 12, whiteSpace: 'nowrap' }}>
        {translate(t, STATUS_LABEL_KEYS[status] ?? status)}
      </span>
    );
  };

  const catalogueTab = (
    <>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <Search
          allowClear
          placeholder={translate(t, TRANSLATIONS.RATE_CATALOG_SEARCH_PLACEHOLDER)}
          onSearch={(value) => {
            const term = value.trim() || undefined;
            setSearch(term);
            loadItems(term);
          }}
          style={{ flex: 1, minWidth: 220, maxWidth: 420 }}
        />
        <Button shape="round" icon={<IconPlus size={16} />} onClick={openNewItem}>
          {translate(t, TRANSLATIONS.RATE_ITEM_ADD)}
        </Button>
      </div>

      {items.length === 0 ? (
        <EmptyState
          icon={<IconFileSpreadsheet size={22} />}
          title={translate(t, TRANSLATIONS.RATE_CATALOG_EMPTY_TITLE)}
          description={translate(t, TRANSLATIONS.RATE_CATALOG_EMPTY_DESCRIPTION)}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {items.map((item) => (
            <Card key={item.id} size="small" style={{ borderRadius: 14 }} styles={{ body: { padding: 14 } }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <Text strong style={{ display: 'block' }}>{item.title}</Text>
                  {item.subtitle && (
                    <Text type="secondary" style={{ fontSize: 13, display: 'block' }}>{item.subtitle}</Text>
                  )}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                    {item.reference && <Tag style={{ marginInlineEnd: 0, borderRadius: 999 }}>{item.reference}</Tag>}
                    <Tag style={{ marginInlineEnd: 0, borderRadius: 999 }}>{translate(t, KIND_LABEL_KEYS[item.kind])}</Tag>
                    {/* Only worth a badge when it means something: a price
                        this provider fixed by hand is the one an upload will
                        not touch. */}
                    {item.source !== 'Uploaded' && (
                      <Tag color="gold" style={{ marginInlineEnd: 0, borderRadius: 999 }}>
                        {translate(t, TRANSLATIONS.RATE_ITEM_SOURCE_YOURS)}
                      </Tag>
                    )}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <Text strong style={{ fontSize: 15, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {item.unitPrice.toFixed(2)} {item.currencyCode} / {item.unit}
                  </Text>
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 8 }}>
                    <Button size="small" icon={<IconPencil size={14} />} onClick={() => openEditItem(item)} />
                    <Button size="small" danger icon={<IconTrash size={14} />} onClick={() => setDeletingItem(item)} />
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </>
  );

  const documentsTab = documents.length === 0 ? (
    <Empty description={translate(t, TRANSLATIONS.RATE_SHEET_NONE_YET)} />
  ) : (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {documents.map((doc) => (
        <Card key={doc.id} size="small" style={{ borderRadius: 14 }} styles={{ body: { padding: 14 } }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <Text strong style={{ display: 'block', wordBreak: 'break-word' }}>{doc.fileName}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {dayjs(doc.uploadedAt).format('DD/MM/YYYY HH:mm')}
                {doc.status === 'Confirmed' && ` · ${translate(t, TRANSLATIONS.RATE_SHEET_ITEMS_CONFIRMED, { count: doc.itemsConfirmed })}`}
              </Text>
              {doc.failureReason && (
                <Paragraph type="danger" style={{ fontSize: 13, marginTop: 6, marginBottom: 0 }}>
                  {doc.failureReason}
                </Paragraph>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {statusPill(doc.status)}
              {doc.status === 'PendingReview' && (
                <Button
                  type="primary"
                  shape="round"
                  size="small"
                  onClick={() => navigate(`/heavy-duty-machinery/my-rates/${doc.id}/review`)}
                >
                  {translate(t, TRANSLATIONS.RATE_SHEET_REVIEW_CTA)}
                </Button>
              )}
              <Button size="small" danger icon={<IconTrash size={14} />} onClick={() => setDeletingDocument(doc)} />
            </div>
          </div>
        </Card>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <PageHeaderRow
        title={translate(t, TRANSLATIONS.RATE_CATALOG_TITLE)}
        subtitle={translate(t, TRANSLATIONS.RATE_CATALOG_SUBTITLE)}
        actions={
          <Button type="primary" icon={<IconUpload size={16} />} onClick={() => setUploadOpen(true)}>
            {translate(t, TRANSLATIONS.RATE_SHEET_UPLOAD_CTA)}
          </Button>
        }
      />

      <Tabs
        items={[
          {
            key: 'catalog',
            label: `${translate(t, TRANSLATIONS.RATE_CATALOG_TAB_PRICES)}${items.length ? ` (${items.length})` : ''}`,
            children: catalogueTab,
          },
          {
            key: 'documents',
            label: translate(t, TRANSLATIONS.RATE_CATALOG_TAB_DOCUMENTS),
            children: documentsTab,
          },
          {
            // Here rather than on its own page because a provider thinks of
            // this as the same thing: how they quote. The prices are one half
            // of it, what they need to know before they can name one is the
            // other.
            key: 'guide',
            label: translate(t, TRANSLATIONS.ASSISTANT_GUIDE_TAB),
            children: <AssistantGuideEditor />,
          },
          {
            // The third thing a provider decides about how they quote, after
            // what they charge and what they need to know: whether getting them
            // to look at it costs anything.
            key: 'visit',
            label: translate(t, TRANSLATIONS.VISIT_FEE_TAB),
            children: <VisitFeeEditor />,
          },
        ]}
      />

      <Modal
        open={uploadOpen}
        title={translate(t, TRANSLATIONS.RATE_SHEET_UPLOAD_TITLE)}
        onCancel={() => setUploadOpen(false)}
        onOk={handleUpload}
        okText={translate(t, TRANSLATIONS.RATE_SHEET_UPLOAD_CONFIRM)}
        confirmLoading={uploading}
        okButtonProps={{ disabled: !pendingFile || pricesIncludeVat === null }}
        width={isMobile ? undefined : 520}
      >
        <Paragraph type="secondary" style={{ fontSize: 13 }}>
          {translate(t, TRANSLATIONS.RATE_SHEET_UPLOAD_DESCRIPTION)}
        </Paragraph>

        <Upload.Dragger
          maxCount={1}
          accept=".pdf,.csv,.xls,.xlsx,image/*"
          beforeUpload={() => false}
          fileList={pendingFile ? [pendingFile] : []}
          onChange={({ fileList }) => setPendingFile(fileList[0] ?? null)}
          style={{ borderRadius: 14, marginBottom: 18 }}
        >
          <p style={{ margin: '10px 0' }}>
            <IconUpload size={26} style={{ color: TEAL }} />
          </p>
          <Text style={{ display: 'block' }}>{translate(t, TRANSLATIONS.RATE_SHEET_UPLOAD_DROP)}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {translate(t, TRANSLATIONS.RATE_SHEET_UPLOAD_FORMATS)}
          </Text>
        </Upload.Dragger>

        {/* Asked, never assumed, and required before the file will go: see
            this component's own doc comment for what guessing it wrong does
            silently to somebody's whole catalogue. */}
        <Text strong style={{ display: 'block', marginBottom: 8 }}>
          {translate(t, TRANSLATIONS.RATE_SHEET_VAT_QUESTION)}
        </Text>
        <Radio.Group
          value={pricesIncludeVat}
          onChange={(e) => setPricesIncludeVat(e.target.value)}
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
        >
          <Radio value={false}>{translate(t, TRANSLATIONS.RATE_SHEET_VAT_EXCLUDED)}</Radio>
          <Radio value>{translate(t, TRANSLATIONS.RATE_SHEET_VAT_INCLUDED)}</Radio>
        </Radio.Group>
      </Modal>

      <Modal
        open={itemModalOpen}
        title={translate(t, editing ? TRANSLATIONS.RATE_ITEM_EDIT_TITLE : TRANSLATIONS.RATE_ITEM_ADD)}
        onCancel={() => setItemModalOpen(false)}
        onOk={handleSaveItem}
        okText={translate(t, TRANSLATIONS.RATE_ITEM_SAVE)}
        confirmLoading={savingItem}
      >
        <Text style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>
          {translate(t, TRANSLATIONS.RATE_ITEM_TITLE_LABEL)}
        </Text>
        <Input
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          style={{ borderRadius: 999, marginBottom: 12 }}
        />

        <Text style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>
          {translate(t, TRANSLATIONS.RATE_ITEM_REFERENCE_LABEL)}
        </Text>
        <Input
          value={draft.reference}
          onChange={(e) => setDraft({ ...draft, reference: e.target.value })}
          style={{ borderRadius: 999, marginBottom: 12 }}
        />

        <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 130 }}>
            <Text style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>
              {translate(t, TRANSLATIONS.RATE_ITEM_KIND_LABEL)}
            </Text>
            <Select
              value={draft.kind}
              onChange={(kind: RateItemKind) => setDraft({ ...draft, kind })}
              style={{ width: '100%' }}
              options={(Object.keys(KIND_LABEL_KEYS) as RateItemKind[]).map((kind) => ({
                value: kind,
                label: translate(t, KIND_LABEL_KEYS[kind]),
              }))}
            />
          </div>
          <div style={{ flex: 1, minWidth: 100 }}>
            <Text style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>
              {translate(t, TRANSLATIONS.RATE_ITEM_UNIT_LABEL)}
            </Text>
            <Select
              value={draft.unit}
              onChange={(unit: string) => setDraft({ ...draft, unit })}
              style={{ width: '100%' }}
              options={RATE_ITEM_UNITS.map((unit) => ({ value: unit, label: unit }))}
            />
          </div>
          <div style={{ flex: 1, minWidth: 120 }}>
            <Text style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>
              {translate(t, TRANSLATIONS.RATE_ITEM_PRICE_LABEL)}
            </Text>
            <InputNumber
              value={draft.unitPrice}
              onChange={(value) => setDraft({ ...draft, unitPrice: value ?? 0 })}
              min={0}
              step={0.5}
              addonAfter="€"
              style={{ width: '100%' }}
            />
          </div>
        </div>

        <Text type="secondary" style={{ fontSize: 12 }}>
          {translate(t, TRANSLATIONS.RATE_ITEM_PRICE_VAT_NOTE)}
        </Text>
      </Modal>

      <ConfirmBottomSheet
        open={deletingItem !== null}
        title={translate(t, TRANSLATIONS.RATE_ITEM_DELETE_TITLE)}
        description={deletingItem?.title}
        confirmText={translate(t, TRANSLATIONS.RATE_ITEM_DELETE_CONFIRM)}
        cancelText={translate(t, TRANSLATIONS.RATE_ITEM_DELETE_CANCEL)}
        onConfirm={handleDeleteItem}
        onClose={() => setDeletingItem(null)}
      />

      <ConfirmBottomSheet
        open={deletingDocument !== null}
        title={translate(t, TRANSLATIONS.RATE_SHEET_DELETE_TITLE)}
        description={translate(t, TRANSLATIONS.RATE_SHEET_DELETE_DESCRIPTION)}
        confirmText={translate(t, TRANSLATIONS.RATE_ITEM_DELETE_CONFIRM)}
        cancelText={translate(t, TRANSLATIONS.RATE_ITEM_DELETE_CANCEL)}
        onConfirm={handleDeleteDocument}
        onClose={() => setDeletingDocument(null)}
      />
    </div>
  );
};

const MyRateCatalog: React.FC = () => (
  <AppLayout>
    <MyRateCatalogContent />
  </AppLayout>
);

export default MyRateCatalog;
